import { useCallback, useRef, useState } from "react";
import { Download, File, FileText, Image, Music, Pencil, Trash2, Upload, Video, X } from "lucide-react";

interface FileEntry {
  _id: string;
  filename: string;
  mimeType: string;
  size: number;
  _creationTime: number;
  url: string | undefined;
}

interface FileManagerContentProps {
  files: FileEntry[];
  isUploading: boolean;
  uploadError?: string | null;
  onUpload: (file: File) => void;
  onRename: (fileId: string, newName: string) => void;
  onDelete: (fileId: string) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith("image/")) return <Image className="h-4 w-4 shrink-0 text-purple-500 dark:text-purple-400" />;
  if (mimeType.startsWith("video/")) return <Video className="h-4 w-4 shrink-0 text-pink-500 dark:text-pink-400" />;
  if (mimeType.startsWith("audio/")) return <Music className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />;
  if (mimeType === "application/pdf" || mimeType.startsWith("text/")) return <FileText className="h-4 w-4 shrink-0 text-blue-500 dark:text-blue-400" />;
  return <File className="h-4 w-4 shrink-0 text-black/50 dark:text-white/50" />;
}

export function FileManagerContent({
  files,
  isUploading,
  uploadError,
  onUpload,
  onRename,
  onDelete,
}: FileManagerContentProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) onUpload(file);
    },
    [onUpload],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) onUpload(file);
      e.target.value = "";
    },
    [onUpload],
  );

  const startRename = useCallback((id: string, currentName: string) => {
    setEditingId(id);
    setEditValue(currentName);
  }, []);

  const commitRename = useCallback(
    (id: string) => {
      const trimmed = editValue.trim();
      if (trimmed && trimmed.length <= 255) {
        onRename(id, trimmed);
      }
      setEditingId(null);
    },
    [editValue, onRename],
  );

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="flex flex-col gap-3">
      {/* Drop zone */}
      <div
        className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 transition-colors ${
          dragOver
            ? "border-brand-coral bg-brand-coral/10"
            : "border-black/20 bg-black/5 hover:border-black/40 dark:border-white/15 dark:bg-white/5 dark:hover:border-white/30"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />
        {isUploading ? (
          <span className="text-sm text-black/50 dark:text-white/50">Uploading...</span>
        ) : (
          <>
            <Upload className="mb-1 h-5 w-5 text-black/40 dark:text-white/40" />
            <span className="text-sm text-black/50 dark:text-white/50">
              Drop files here or click to browse
            </span>
            <span className="text-xs text-black/40 dark:text-white/30">20MB max</span>
          </>
        )}
      </div>

      {uploadError && (
        <p className="text-xs text-red-600 dark:text-red-400">{uploadError}</p>
      )}

      {/* File list */}
      {files.length === 0 ? (
        <p className="py-6 text-center text-sm text-black/40 dark:text-white/30">
          No files yet — drop files here to get started
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {files.map((f) => (
            <div
              key={f._id}
              className="group flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-black/5 dark:hover:bg-white/5"
            >
              <FileIcon mimeType={f.mimeType} />

              {editingId === f._id ? (
                <input
                  className="min-w-0 flex-1 rounded border border-black/20 bg-white px-1.5 py-0.5 text-sm text-black outline-none dark:border-white/20 dark:bg-neutral-900 dark:text-white"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename(f._id);
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  onBlur={() => commitRename(f._id)}
                  autoFocus
                />
              ) : (
                <span className="min-w-0 flex-1 truncate text-sm text-black dark:text-white/80">
                  {f.filename}
                </span>
              )}

              <span className="shrink-0 text-xs text-black/40 dark:text-white/30">
                {formatBytes(f.size)}
              </span>

              {/* Actions — visible on hover */}
              <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                {f.url && (
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="rounded p-1 text-black/40 hover:bg-black/10 hover:text-black/80 dark:text-white/40 dark:hover:bg-white/10 dark:hover:text-white/70"
                    title="Download"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => startRename(f._id, f.filename)}
                  className="rounded p-1 text-black/40 hover:bg-black/10 hover:text-black/80 dark:text-white/40 dark:hover:bg-white/10 dark:hover:text-white/70"
                  title="Rename"
                >
                  {editingId === f._id ? (
                    <X className="h-3.5 w-3.5" />
                  ) : (
                    <Pencil className="h-3.5 w-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Delete "${f.filename}"?`)) onDelete(f._id);
                  }}
                  className="rounded p-1 text-black/40 hover:bg-red-500/20 hover:text-red-500 dark:text-white/40 dark:hover:text-red-400"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      {files.length > 0 && (
        <p className="text-xs text-black/40 dark:text-white/30">
          {files.length} file{files.length !== 1 ? "s" : ""} · {formatBytes(totalSize)} used
        </p>
      )}
    </div>
  );
}
